/**
 * Analytics utility for tracking user interactions and performance metrics
 * This module provides a flexible analytics implementation that can work with
 * various analytics providers (Google Analytics, Plausible, custom solutions)
 */

// Configuration object for analytics
const analyticsConfig = {
  // Whether analytics is enabled
  enabled: process.env.REACT_APP_ANALYTICS_ENABLED === 'true',
  // Analytics provider (google, plausible, custom)
  provider: process.env.REACT_APP_ANALYTICS_PROVIDER || 'custom',
  // Tracking ID for the analytics provider
  trackingId: process.env.REACT_APP_ANALYTICS_TRACKING_ID,
  // Whether to track performance metrics
  trackPerformance: true,
  // Whether to track user interactions
  trackInteractions: true,
  // Whether to track errors
  trackErrors: true,
  // Whether to track page views
  trackPageViews: true,
};

/**
 * Initialize analytics based on the configured provider
 */
export const initAnalytics = () => {
  if (!analyticsConfig.enabled) {
    console.log('Analytics is disabled');
    return;
  }

  switch (analyticsConfig.provider) {
    case 'google':
      initGoogleAnalytics();
      break;
    case 'plausible':
      initPlausibleAnalytics();
      break;
    case 'custom':
    default:
      initCustomAnalytics();
      break;
  }

  // Initialize performance tracking if enabled
  if (analyticsConfig.trackPerformance) {
    initPerformanceTracking();
  }
};

/**
 * Initialize Google Analytics
 */
const initGoogleAnalytics = () => {
  // Load Google Analytics script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${analyticsConfig.trackingId}`;
  document.head.appendChild(script);

  // Initialize Google Analytics
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  gtag('js', new Date());
  gtag('config', analyticsConfig.trackingId);

  // Store gtag function for later use
  window.gtag = gtag;
};

/**
 * Initialize Plausible Analytics
 */
const initPlausibleAnalytics = () => {
  // Load Plausible Analytics script
  const script = document.createElement('script');
  script.async = true;
  script.defer = true;
  script.dataset.domain = window.location.hostname;
  script.src = 'https://plausible.io/js/plausible.js';
  document.head.appendChild(script);
};

/**
 * Initialize custom analytics solution
 */
const initCustomAnalytics = () => {
  // Simple custom analytics implementation that logs events to console
  // In a production environment, this would send data to a backend API
  window.customAnalytics = {
    trackEvent: (category, action, label, value) => {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Analytics Event:', { category, action, label, value });
      }
      // In production, this would send data to a backend API
    },
    trackPageView: (path) => {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Page View:', path);
      }
      // In production, this would send data to a backend API
    },
    trackError: (error, errorInfo) => {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Error:', error, errorInfo);
      }
      // In production, this would send data to a backend API
    },
    trackPerformance: (metric) => {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Performance Metric:', metric);
      }
      // In production, this would send data to a backend API
    },
  };
};

/**
 * Initialize performance tracking using the Web Vitals API
 */
const initPerformanceTracking = () => {
  if ('PerformanceObserver' in window) {
    // Track Core Web Vitals
    import('web-vitals').then(({ getCLS, getFID, getLCP, getFCP, getTTFB }) => {
      getCLS(sendToAnalytics);
      getFID(sendToAnalytics);
      getLCP(sendToAnalytics);
      getFCP(sendToAnalytics);
      getTTFB(sendToAnalytics);
    });
  }
};

/**
 * Send performance metrics to the configured analytics provider
 * @param {Object} metric - Performance metric object
 */
const sendToAnalytics = (metric) => {
  const { name, value } = metric;
  
  switch (analyticsConfig.provider) {
    case 'google':
      if (window.gtag) {
        window.gtag('event', name, {
          event_category: 'Web Vitals',
          event_label: name,
          value: Math.round(name === 'CLS' ? value * 1000 : value),
          non_interaction: true,
        });
      }
      break;
    case 'plausible':
      if (window.plausible) {
        window.plausible('Performance', {
          props: {
            metric: name,
            value: Math.round(name === 'CLS' ? value * 1000 : value),
          },
        });
      }
      break;
    case 'custom':
    default:
      if (window.customAnalytics) {
        window.customAnalytics.trackPerformance({
          name,
          value: Math.round(name === 'CLS' ? value * 1000 : value),
        });
      }
      break;
  }
};

/**
 * Track a page view
 * @param {string} path - Page path
 * @param {string} title - Page title
 */
export const trackPageView = (path, title) => {
  if (!analyticsConfig.enabled || !analyticsConfig.trackPageViews) {
    return;
  }

  switch (analyticsConfig.provider) {
    case 'google':
      if (window.gtag) {
        window.gtag('event', 'page_view', {
          page_path: path,
          page_title: title,
        });
      }
      break;
    case 'plausible':
      if (window.plausible) {
        window.plausible('pageview', {
          props: { path, title },
        });
      }
      break;
    case 'custom':
    default:
      if (window.customAnalytics) {
        window.customAnalytics.trackPageView(path);
      }
      break;
  }
};

/**
 * Track a user interaction event
 * @param {string} category - Event category
 * @param {string} action - Event action
 * @param {string} label - Event label
 * @param {number} value - Event value
 */
export const trackEvent = (category, action, label = null, value = null) => {
  if (!analyticsConfig.enabled || !analyticsConfig.trackInteractions) {
    return;
  }

  switch (analyticsConfig.provider) {
    case 'google':
      if (window.gtag) {
        window.gtag('event', action, {
          event_category: category,
          event_label: label,
          value: value,
        });
      }
      break;
    case 'plausible':
      if (window.plausible) {
        window.plausible(action, {
          props: {
            category,
            label,
            value,
          },
        });
      }
      break;
    case 'custom':
    default:
      if (window.customAnalytics) {
        window.customAnalytics.trackEvent(category, action, label, value);
      }
      break;
  }
};

/**
 * Track an error
 * @param {Error} error - Error object
 * @param {Object} errorInfo - Additional error information
 */
export const trackError = (error, errorInfo = {}) => {
  if (!analyticsConfig.enabled || !analyticsConfig.trackErrors) {
    return;
  }

  switch (analyticsConfig.provider) {
    case 'google':
      if (window.gtag) {
        window.gtag('event', 'exception', {
          description: error.message,
          fatal: true,
        });
      }
      break;
    case 'plausible':
      if (window.plausible) {
        window.plausible('error', {
          props: {
            message: error.message,
            stack: error.stack,
            ...errorInfo,
          },
        });
      }
      break;
    case 'custom':
    default:
      if (window.customAnalytics) {
        window.customAnalytics.trackError(error, errorInfo);
      }
      break;
  }
};

/**
 * Analytics hook for React components
 * @returns {Object} - Analytics methods
 */
export const useAnalytics = () => {
  return {
    trackPageView,
    trackEvent,
    trackError,
  };
};

export default {
  initAnalytics,
  trackPageView,
  trackEvent,
  trackError,
  useAnalytics,
};
